import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';
import { Panel, Loader, EmptyState, useToast, errText, useCaptchaConfirm } from '../ui';

export default function TenantList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [confirm, confirmNode] = useCaptchaConfirm();

  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [busyCode, setBusyCode] = useState(null);

  const load = async () => {
    setRows(null);
    try {
      const res = await tenantApi.getAllTenants();
      setRows(res.data || []);
    } catch (err) {
      toast.error(errText(err));
      setRows([]);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = (rows || []).filter((r) => {
    const s = q.toLowerCase();
    return (
      (r.clientCode || '').toLowerCase().includes(s) ||
      (r.clientName || '').toLowerCase().includes(s) ||
      (r.emailId || '').toLowerCase().includes(s)
    );
  });

  const handleDelete = async (clientCode) => {
    const ok = await confirm(
      `Remove registration for "${clientCode}" from the Master DB? This does NOT drop the tenant database — use the danger zone on the tenant detail page for that.`
    );
    if (!ok) return;
    setBusyCode(clientCode);
    try {
      await tenantApi.deleteTenant(clientCode);
      toast.success(`Registration for ${clientCode} removed.`);
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusyCode(null);
    }
  };

  return (
    <>
      {confirmNode}
      <h1 className="page-title">tenant_list</h1>
      <p className="page-sub">GET /v1/admin/tenants/configure — all registered tenants in the Master DB</p>

      <Panel
        title="// REGISTERED_TENANTS"
        right={
          <button className="term-btn term-btn--solid" onClick={() => navigate('/admin/tenants/new')}>
            + new_tenant
          </button>
        }
      >
        <div className="toolbar">
          <input
            className="term-input"
            style={{ maxWidth: 320 }}
            placeholder="grep clientCode / clientName / email..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button className="term-btn term-btn--ghost term-btn--sm" onClick={load}>
            refresh
          </button>
        </div>

        {rows === null && <Loader label="fetching tenant registry" />}

        {rows !== null && filtered.length === 0 && (
          <EmptyState>No tenants match. Run &quot;+ new_tenant&quot; to provision one.</EmptyState>
        )}

        {rows !== null && filtered.length > 0 && (
          <table className="term-table">
            <thead>
              <tr>
                <th>clientCode</th>
                <th>clientName</th>
                <th>emailId</th>
                <th className="num">noOfUsers</th>
                <th>expiryDate</th>
                <th>status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.clientCode}>
                  <td>{t.clientCode}</td>
                  <td>{t.clientName}</td>
                  <td>{t.emailId}</td>
                  <td className="num">{t.noOfUsers}</td>
                  <td>{t.expiryDate ? String(t.expiryDate).slice(0, 10) : '—'}</td>
                  <td>
                    <span className={`badge ${t.isPending ? 'badge--warn' : 'badge--ok'}`}>
                      {t.isPending ? 'PENDING' : 'ACTIVE'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        className="term-btn term-btn--sm"
                        onClick={() => navigate(`/admin/tenants/${t.clientCode}`)}
                      >
                        open
                      </button>
                      <button
                        className="term-btn term-btn--sm term-btn--danger"
                        disabled={busyCode === t.clientCode}
                        onClick={() => handleDelete(t.clientCode)}
                      >
                        {busyCode === t.clientCode ? '...' : 'remove'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}
